import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * iOS 오버레이 키보드 대응 (PLAN 6-3장, 8장).
 *
 * iOS Safari는 키보드가 열려도 layout viewport가 변하지 않아
 * `position: fixed` 요소가 키보드 뒤에 숨겨진다. visualViewport를
 * 구독해 실제 가시 영역 높이를 CSS 변수로 노출한다.
 *
 * 입력창 여백이 화면 아래로 벌어지지 않도록 두 가지로 강건화한다.
 *
 *  1. 값은 편집기(INPUT/TEXTAREA/contenteditable)가 포커스 중일 때만
 *     신뢰한다 — 포커스가 없는데 남아 있는 값은 닫히던 키보드의 중간
 *     측정치 같은 잔재이므로 0으로 돌린다.
 *  2. visualViewport 이벤트뿐 아니라 resize/orientation/focus/포인터
 *     다운/visibility 신호에서도 "이벤트 시점"에 현재값을 다시 읽는다.
 *     visualViewport.height는 이벤트가 없어도 항상 실시간 값이라,
 *     키보드 닫힘 resize를 놓쳤더라도 다음 터치에서 스스로 복구된다.
 *     (MDN: env(safe-area-inset-*) / Chrome edge-to-edge 가이드 참고)
 */
export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let frame = 0;
    let settle = 0;
    let focusPoll = 0;
    let lastKb = -1;

    const editableFocused = () => {
      const el = document.activeElement;
      return (
        el instanceof HTMLElement &&
        (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
      );
    };

    const sync = () => {
      // layoutViewport 높이 - visualViewport 높이 = 키보드(인풋 액세서리 포함) 높이
      const raw = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // 키보드가 뷰포트의 절반을 넘는 일은 없다 — 비정상 측정치 가드
      const keyboard = editableFocused() ? Math.min(raw, Math.round(window.innerHeight * 0.6)) : 0;
      // 값이 그대로면 DOM 쓰기 생략 — 포커스 중 주기 재확인의 스타일 재계산 낭비 방지
      if (keyboard === lastKb) return;
      lastKb = keyboard;
      setHeight(keyboard);
      document.documentElement.style.setProperty('--kb-height', `${keyboard}px`);

      // 키보드가 열리면 iOS가 스크롤을 밀어내므로 원위치 복구
      if (keyboard > 0) window.scrollTo(0, 0);
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
      // iOS는 키보드 애니메이션이 끝난 뒤 레이아웃을 한 번 더 갱신한다 — 그 시점에 재확인
      window.clearTimeout(settle);
      settle = window.setTimeout(sync, 300);
    };

    const stopFocusPoll = () => {
      if (focusPoll) {
        window.clearInterval(focusPoll);
        focusPoll = 0;
      }
    };

    const startFocusPoll = () => {
      if (focusPoll) return;
      // QuickType·연락처 자동완성 행은 visualViewport resize 없이 토글되는 경우가 있어
      // 포커스 동안에는 주기적으로 재측정한다 (값 불변 시 조기 반환)
      focusPoll = window.setInterval(sync, 400);
    };

    const syncFocusPolling = () => {
      if (editableFocused()) startFocusPoll();
      else stopFocusPoll();
    };

    const viewportSignal = () => {
      schedule();
      syncFocusPolling();
    };

    sync();
    syncFocusPolling();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('focusin', viewportSignal, true);
    document.addEventListener('focusout', viewportSignal, true);
    // 다음 터치마다 실시간 값 재확인 — 놓친 닫힘 이벤트도 사용자의 다음 동작에서 복구된다
    document.addEventListener('pointerdown', schedule, { capture: true, passive: true });
    document.addEventListener('visibilitychange', viewportSignal);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      stopFocusPoll();
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.removeEventListener('pageshow', schedule);
      document.removeEventListener('focusin', schedule, true);
      document.removeEventListener('focusout', schedule, true);
      document.removeEventListener('pointerdown', schedule, { capture: true });
      document.removeEventListener('visibilitychange', schedule);
      document.documentElement.style.setProperty('--kb-height', '0px');
    };
  }, []);

  return height;
}

export type ThemePreference = 'system' | 'light' | 'dark';

/** 테마 적용 — 시스템 설정을 따르되 사용자가 명시하면 고정 */
export function useTheme(preference: ThemePreference = 'system') {
  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', preference);
  }, [preference]);
}

/** 연결 상태 감시 — 오프라인 배너 표시용 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  return online;
}

/**
 * 스르코파스 방지 타이머 (PLAN 5-8-9장).
 * 포그라운드에서 일정 시간 무조작이면 활성 대화방 리스너를 해제해
 * Firestore 읽기와 배터리를 절약한다.
 */
export function useIdleTimeout(onIdle: () => void, ms: number, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    let timer = window.setTimeout(onIdle, ms);
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(onIdle, ms);
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'visibilitychange'] as const;
    for (const e of events) window.addEventListener(e, reset, { passive: true });
    return () => {
      window.clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, reset);
    };
  }, [onIdle, ms, enabled]);
}

/**
 * iOS standalone(설치형 PWA) 뷰포트 높이 보정 (PLAN 6-3장).
 *
 * Apple Web App에서는 layout viewport가 화면 전체를 덮지만( 노치 포함,
 * viewport-fit=cover), viewport 단위(100dvh/100vh)는 status bar 오버레이를
 * 제외한 값을 돌려준다 — WebKit의 dvh는 visual viewport 기준이라status bar
 * 높이(예: iPhone 16 59pt)만큼 짧다. 그 결과 .app-shell이 화면보다 짧아져
 * 하단에 고정된 탭 독·입력창이 화면 가장자리보다 위에 떠 있었다.
 *
 * 설치형일 때만 `--app-height`를 layout viewport 전체 높이(innerHeight)로
 * 고정해 하단 요소가 가장자리에 붙게 하고, 브라우저 탭은 주소바 높이에
 * 반응하는 `100dvh`를 그대로 쓴다. 키보드는 오버레이라 innerHeight가
 * 줄지 않으므로 키보드 열림 상태에서도 값이 유지된다.
 *
 * (MDN viewport meta: viewport-fit=cover / MDN CSS viewport units: dvh,
 *  WebKit "Designing Websites for iPhone X")
 */
export function useStandaloneAppHeight(): void {
  useLayoutEffect(() => {
    const standalone =
      ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches);

    const isIOS =
      typeof navigator !== 'undefined' &&
      (/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

    // If it's neither standalone nor iOS, don't intervene (Android/desktop browsers use native 100dvh).
    if (!standalone && !isIOS) return;

    const root = document.documentElement;
    const sync = () => {
      const vv = window.visualViewport;
      // In iOS standalone PWA with viewport-fit=cover & black-translucent status bar,
      // the web view covers the entire hardware display (screen.height).
      // innerHeight or visualViewport may report only the safe area or exclude the status bar (e.g. 59pt short on iPhone 16).
      // Therefore, Math.max with window.screen.height ensures the layout viewport fills 100% of the display edge-to-edge.
      // In iOS Safari tabs (non-standalone), visualViewport.height accurately measures the visible area between toolbars.
      // On Android (e.g. Galaxy S26), screen.height includes the OS status & navigation bars outside the PWA viewport,
      // so forcing screen.height causes .app-shell to overflow and pushes the bottom nav off-screen.
      let height: number;
      if (isIOS) {
        if (standalone) {
          const screenH = typeof window.screen?.height === 'number' ? window.screen.height : 0;
          height = Math.max(window.innerHeight, vv ? vv.height + vv.offsetTop : 0, screenH);
        } else {
          height = vv ? vv.height : window.innerHeight;
        }
      } else {
        height = Math.max(window.innerHeight, vv ? vv.height + vv.offsetTop : 0);
      }
      root.style.setProperty('--app-height', `${Math.round(height)}px`);
    };

    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    window.addEventListener('pageshow', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      window.removeEventListener('pageshow', sync);
      document.removeEventListener('visibilitychange', sync);
      root.style.removeProperty('--app-height');
    };
  }, []);
}
